/**
 * Character indexing module - builds position-based indices for fast lookup
 * Implements O(1) search by 2nd/3rd character position
 */

import { WordIndex } from '@/types/dictionary';
import { SearchIndex } from '@/types/search';
import { Trie } from './trie';

/**
 * Build comprehensive search indices from word data
 * @param words - Array of WordIndex objects
 * @returns SearchIndex with all indices populated
 */
export function buildSearchIndex(words: WordIndex[]): SearchIndex {
  const prefixTrie = new Trie();
  const position2Index = new Map<string, WordIndex[]>();
  const position3Index = new Map<string, WordIndex[]>();
  const position4Index = new Map<string, WordIndex[]>();
  const anyPositionIndex = new Map<string, Set<WordIndex>>();

  for (const word of words) {
    // Build prefix trie
    prefixTrie.insert(word);

    const wordLower = word.word.toLowerCase();

    // Build position-specific indices
    if (wordLower.length >= 2) {
      const char2 = wordLower[1]!;
      if (!position2Index.has(char2)) {
        position2Index.set(char2, []);
      }
      position2Index.get(char2)!.push(word);
    }

    if (wordLower.length >= 3) {
      const char3 = wordLower[2]!;
      if (!position3Index.has(char3)) {
        position3Index.set(char3, []);
      }
      position3Index.get(char3)!.push(word);
    }

    if (wordLower.length >= 4) {
      const char4 = wordLower[3]!;
      if (!position4Index.has(char4)) {
        position4Index.set(char4, []);
      }
      position4Index.get(char4)!.push(word);
    }

    // Build any-position index (for advanced search)
    for (let i = 0; i < wordLower.length; i++) {
      const char = wordLower[i]!;
      if (!anyPositionIndex.has(char)) {
        anyPositionIndex.set(char, new Set());
      }
      anyPositionIndex.get(char)!.add(word);
    }
  }

  // Sort all position indices by frequency (descending)
  const sortByFrequency = (a: WordIndex, b: WordIndex) => b.frequency - a.frequency;

  position2Index.forEach(words => words.sort(sortByFrequency));
  position3Index.forEach(words => words.sort(sortByFrequency));
  position4Index.forEach(words => words.sort(sortByFrequency));

  return {
    prefixTrie: prefixTrie.getRoot(),
    position2Index,
    position3Index,
    position4Index,
    anyPositionIndex,
  };
}

/**
 * Search by second character position
 * @param char - Character to search for in 2nd position
 * @param index - SearchIndex to query
 * @param maxResults - Maximum results to return
 * @returns Array of matching WordIndex objects
 */
export function searchByPosition2(
  char: string,
  index: SearchIndex,
  maxResults: number = 20
): WordIndex[] {
  const results = index.position2Index.get(char.toLowerCase()) || [];
  return results.slice(0, maxResults);
}

/**
 * Search by third character position
 * @param char - Character to search for in 3rd position
 * @param index - SearchIndex to query
 * @param maxResults - Maximum results to return
 * @returns Array of matching WordIndex objects
 */
export function searchByPosition3(
  char: string,
  index: SearchIndex,
  maxResults: number = 20
): WordIndex[] {
  const results = index.position3Index.get(char.toLowerCase()) || [];
  return results.slice(0, maxResults);
}

/**
 * Search by specific character at specific position
 * @param char - Character to search for
 * @param position - Position (2, 3, or 4)
 * @param index - SearchIndex to query
 * @param maxResults - Maximum results to return
 * @returns Array of matching WordIndex objects
 */
export function searchByPosition(
  char: string,
  position: number,
  index: SearchIndex,
  maxResults: number = 20
): WordIndex[] {
  const lowerChar = char.toLowerCase();

  switch (position) {
    case 2:
      return searchByPosition2(lowerChar, index, maxResults);
    case 3:
      return searchByPosition3(lowerChar, index, maxResults);
    case 4:
      const results = index.position4Index.get(lowerChar) || [];
      return results.slice(0, maxResults);
    default:
      console.warn(`Invalid position: ${position}. Use 2, 3, or 4.`);
      return [];
  }
}

/**
 * Combined search: first character + second character
 * Most common use case for 2D index simulation
 * @param firstChar - First character
 * @param secondChar - Second character
 * @param index - SearchIndex to query
 * @param maxResults - Maximum results to return
 * @returns Array of matching WordIndex objects
 */
export function searchByFirstTwo(
  firstChar: string,
  secondChar: string,
  index: SearchIndex,
  maxResults: number = 20
): WordIndex[] {
  const lowerFirst = firstChar.toLowerCase();
  const lowerSecond = secondChar.toLowerCase();

  // Get all words with this second character
  const candidates = index.position2Index.get(lowerSecond) || [];

  // Filter by first character and limit results
  const results = candidates
    .filter(word => word.word.toLowerCase().startsWith(lowerFirst))
    .slice(0, maxResults);

  return results;
}

/**
 * Get statistics about the index
 * @param index - SearchIndex to analyze
 * @returns Statistics object
 */
export function getIndexStats(index: SearchIndex): {
  totalPosition2Chars: number;
  totalPosition3Chars: number;
  totalPosition4Chars: number;
  averageWordsPerPosition2: number;
  averageWordsPerPosition3: number;
  mostCommonPosition2: string;
  mostCommonPosition3: string;
} {
  let totalPosition2Words = 0;
  let totalPosition3Words = 0;
  let maxPosition2Count = 0;
  let maxPosition3Count = 0;
  let mostCommonPosition2 = '';
  let mostCommonPosition3 = '';

  index.position2Index.forEach((words, char) => {
    totalPosition2Words += words.length;
    if (words.length > maxPosition2Count) {
      maxPosition2Count = words.length;
      mostCommonPosition2 = char;
    }
  });

  index.position3Index.forEach((words, char) => {
    totalPosition3Words += words.length;
    if (words.length > maxPosition3Count) {
      maxPosition3Count = words.length;
      mostCommonPosition3 = char;
    }
  });

  return {
    totalPosition2Chars: index.position2Index.size,
    totalPosition3Chars: index.position3Index.size,
    totalPosition4Chars: index.position4Index.size,
    averageWordsPerPosition2:
      index.position2Index.size > 0 ? totalPosition2Words / index.position2Index.size : 0,
    averageWordsPerPosition3:
      index.position3Index.size > 0 ? totalPosition3Words / index.position3Index.size : 0,
    mostCommonPosition2,
    mostCommonPosition3,
  };
}

/**
 * Merge multiple SearchIndex objects (useful when loading data incrementally)
 * @param indices - Array of SearchIndex objects to merge
 * @returns Merged SearchIndex
 */
export function mergeIndices(indices: SearchIndex[]): SearchIndex {
  // For simplicity, rebuild from all collected words
  // In production, this should be more efficient
  return indices[0]!; // Placeholder - implement full merge if needed
}
